import { useEffect, useRef, useState } from 'react';
import type { RentPin } from '../types';
import { VEHICLE_LABELS, PARKING_LABELS, SECURITY_LABELS } from '../types';
import { trackEvent } from '../services/analytics';
import { useTipSeen } from '../hooks/useTour';

interface RentPinFormProps {
  id: string;
  lat: number;
  lng: number;
  onSubmit: (data: Omit<RentPin, 'id' | 'created_at' | 'comment_count'>) => Promise<void>;
}

export default function RentPinForm({ id, lat, lng, onSubmit }: RentPinFormProps) {
  const [formData, setFormData] = useState({
    lat,
    lng,
    vehicle_type: 'car' as const,
    parking_type: 'apartment' as const,
    is_free: false,
    price_hourly: '',
    price_daily: '',
    price_monthly: '',
    is_covered: false,
    security_features: [] as string[],
  });
  const [error, setError] = useState<string | null>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const tip = useTipSeen('form_rent');

  useEffect(() => {
    if (error) {
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [error]);

  const toggleSecurity = (feature: string) => {
    setFormData((prev) => ({
      ...prev,
      security_features: prev.security_features.includes(feature)
        ? prev.security_features.filter((f) => f !== feature)
        : [...prev.security_features, feature],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.is_free) {
      const prices = [formData.price_hourly, formData.price_daily, formData.price_monthly];
      if (prices.some((price) => !price)) {
        trackEvent('add_pin_validation_error', { pin_type: 'rent', error_field: 'prices' });
        setError('Please enter hourly, daily and monthly prices for paid parking');
        return;
      }
    }

    try {
      await onSubmit({
        ...formData,
        price_hourly: formData.is_free ? 0 : formData.price_hourly ? parseFloat(formData.price_hourly) : null,
        price_daily: formData.is_free ? 0 : formData.price_daily ? parseFloat(formData.price_daily) : null,
        price_monthly: formData.is_free ? 0 : formData.price_monthly ? parseFloat(formData.price_monthly) : null,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to submit');
    }
  };

  return (
    <form id={id} onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-purple-50 text-purple-800 text-xs p-2 rounded-lg">
        Help others know real parking prices here: {lat.toFixed(4)}, {lng.toFixed(4)}
      </div>

      {!tip.seen && (
        <div className="bg-purple-50 border border-purple-200 text-purple-900 text-xs p-3 rounded-lg flex items-start gap-2" data-testid="form-tip-rent">
          <span className="text-base leading-none">💡</span>
          <div className="flex-1">
            <div className="font-semibold mb-0.5">Price transparency</div>
            <p className="leading-relaxed text-purple-800">
              This pin is data-only — it does not mean you have a spot to rent. It helps others know what people actually pay in this area.
            </p>
          </div>
          <button
            type="button"
            onClick={tip.mark}
            className="text-purple-700 hover:text-purple-900 font-semibold px-1"
            aria-label="Dismiss tip"
          >
            &times;
          </button>
        </div>
      )}

      {error && (
        <div ref={errorRef} className="bg-red-50 text-red-700 text-sm p-2 rounded-lg">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Vehicle Type</label>
          <select
            value={formData.vehicle_type}
            onChange={(e) => setFormData({ ...formData, vehicle_type: e.target.value as any })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            {Object.entries(VEHICLE_LABELS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Parking Type</label>
          <select
            value={formData.parking_type}
            onChange={(e) => setFormData({ ...formData, parking_type: e.target.value as any })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            {Object.entries(PARKING_LABELS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={formData.is_free}
          onChange={(e) => setFormData({ ...formData, is_free: e.target.checked })}
          className="rounded border-gray-300 text-parksy-600"
        />
        <span className="text-sm font-medium text-gray-700">Free parking</span>
      </label>

      <div className={`grid grid-cols-3 gap-2 ${formData.is_free ? 'opacity-50 pointer-events-none' : ''}`}>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Hourly (₹)</label>
          <input
            type="number"
            value={formData.is_free ? '0' : formData.price_hourly}
            onChange={(e) => setFormData({ ...formData, price_hourly: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="0"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Daily (₹)</label>
          <input
            type="number"
            value={formData.is_free ? '0' : formData.price_daily}
            onChange={(e) => setFormData({ ...formData, price_daily: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="0"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Monthly (₹)</label>
          <input
            type="number"
            value={formData.is_free ? '0' : formData.price_monthly}
            onChange={(e) => setFormData({ ...formData, price_monthly: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="0"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Security</label>
        <div className="flex flex-wrap gap-2">
          {Object.entries(SECURITY_LABELS).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => toggleSecurity(key)}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${
                formData.security_features.includes(key)
                  ? 'bg-parksy-100 border-parksy-500 text-parksy-700'
                  : 'bg-white border-gray-300 text-gray-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={formData.is_covered}
          onChange={(e) => setFormData({ ...formData, is_covered: e.target.checked })}
          className="rounded border-gray-300 text-parksy-600"
        />
        <span className="text-sm text-gray-700">Covered parking</span>
      </label>

      <p className="text-xs text-gray-500">
        This pin is anonymous and only shows price data — not available for rent.
      </p>
    </form>
  );
}
